import type { SaldoDaConta } from "@base/contracts";
import { toast } from "@base/ui";
import { TriangleAlert } from "lucide-react";
import { useEffect, useRef } from "react";
import { navegar } from "../../../shared/navegacao/navegacao";
import { useMonitoramentoDeSaldos } from "../application/conexoes.contexto";
import { contasEmAlerta } from "../domain/contas-em-alerta";
import { reaisDosCentavos } from "../domain/dinheiro";
import { CAMINHO_DO_MONITORAMENTO } from "./acesso-da-monte";

/** Um toast por conta ate este numero; o resto vira um toast de resumo. */
export const CONTAS_NA_PILHA = 6;
const RESUMO = "alerta-de-saldo:resumo";

const idDa = (conta: SaldoDaConta) => `alerta-de-saldo:${conta.plataforma}:${conta.contaId}`;

function detalhe(conta: SaldoDaConta): string {
  if (conta.situacao === "pagamento-pendente") return "Pagamento pendente: a conta parou de veicular.";
  return `Saldo ${reaisDosCentavos(conta.saldoCentavos)}, abaixo do mínimo de ${reaisDosCentavos(conta.minimoCentavos)}.`;
}

/**
 * O X fecha um alerta; arrastar para o lado fecha todos. O sonner chama o mesmo
 * `onDismiss` nos dois casos, entao o evento diz qual foi: o arraste termina num
 * `pointerup`, o X e um `click`.
 */
function foiArrastado(): boolean {
  return globalThis.event?.type === "pointerup";
}

/**
 * Alerta do login: um toast por conta que pede acao (saldo abaixo do minimo ou
 * pagamento pendente), fora as desativadas no monitoramento. Empilhados no
 * canto ate a pessoa fechar; no hover a pilha abre e lista todos. A conta mais
 * urgente fica na frente. Sem conta em alerta, nao aparece nada.
 *
 * `ativo`: so logo depois do login, e so para a equipe Monte (o monitoramento
 * e dela; para os outros a API nem responde).
 */
export function useAlertaDeSaldoNoLogin(ativo: boolean): void {
  const saldos = useMonitoramentoDeSaldos(ativo);
  const mostrado = useRef(false);

  useEffect(() => {
    if (!ativo || mostrado.current || !saldos.data) return;
    mostrado.current = true;
    const contas = contasEmAlerta(saldos.data.contas);
    if (contas.length === 0) return;

    const naPilha = contas.slice(0, CONTAS_NA_PILHA);
    const resto = contas.length - naPilha.length;
    const ids = [...naPilha.map(idDa), ...(resto > 0 ? [RESUMO] : [])];
    const fecharTodos = () => ids.forEach((id) => toast.dismiss(id));
    const aoFechar = () => {
      if (foiArrastado()) fecharTodos();
    };
    const verNoMonitoramento = (
      <button
        type="button"
        className="mt-1 w-fit cursor-pointer font-medium text-foreground underline underline-offset-2"
        onClick={() => {
          fecharTodos();
          navegar(CAMINHO_DO_MONITORAMENTO);
        }}
      >
        Ver no monitoramento
      </button>
    );
    const comum = {
      duration: Number.POSITIVE_INFINITY,
      icon: <TriangleAlert className="text-destructive" aria-hidden="true" />,
      // Duas linhas: icone e X no topo, nao no meio.
      classNames: { toast: "items-start", icon: "mt-0.5" },
      onDismiss: aoFechar,
    };

    // O ultimo toast criado fica na frente: o resumo vai primeiro, a conta mais urgente por ultimo.
    if (resto > 0) {
      toast(`E mais ${resto} ${resto === 1 ? "conta precisa" : "contas precisam"} de atenção no saldo`, {
        ...comum,
        id: RESUMO,
        description: verNoMonitoramento,
      });
    }
    for (const conta of [...naPilha].reverse()) {
      toast(conta.nome, {
        ...comum,
        id: idDa(conta),
        description: (
          <div className="flex flex-col">
            <span className="tabular-nums">{detalhe(conta)}</span>
            {verNoMonitoramento}
          </div>
        ),
      });
    }
  }, [ativo, saldos.data]);
}
